<template>
  <div>
    <!-- 프로그레스 로딩 다이얼로그 -->
    <div v-if="progressLoading.show" class="progress-loading-overlay">
      <div class="progress-loading-dialog">
        <div class="progress-loading-content">
          <div class="loading-spinner"></div>
          <h3 class="loading-title">{{ progressLoading.title }}</h3>
          <p class="loading-message">{{ progressLoading.message }}</p>

          <!-- 프로그레스 바 -->
          <div class="progress-bar-container">
            <div class="progress-bar">
              <div class="progress-bar-fill" :style="{ width: progressLoading.progress + '%' }"></div>
            </div>
            <span class="progress-text">{{ progressLoading.progress }}%</span>
          </div>

          <!-- 재시도 정보 -->
          <div v-if="progressLoading.retryInfo" class="retry-info">
            <small>{{ progressLoading.retryInfo }}</small>
          </div>
        </div>
      </div>
    </div>

    <!-- 작성자 정보 팝업 -->
    <div v-if="authorInfoPopup.show" class="author-info-overlay" @click.self="authorInfoPopup.show = false">
      <div class="author-info-dialog">
        <p class="author-info-row">
          <span class="author-info-label">작성자</span><span class="author-info-value">{{ authorInfoPopup.authorText }}</span>
        </p>
        <p class="author-info-row">
          <span class="author-info-label">상태</span><span class="author-info-value">{{ authorInfoPopup.statusText }}</span>
        </p>
        <div class="author-info-footer">
          <vs-button v-if="authorInfoPopup.documentStatusId !== 1" color="secondary" type="border" style="margin-right: 8px" @click="onClickOpenDocument">문서 이동</vs-button>
          <vs-button color="primary" @click="authorInfoPopup.show = false">확인</vs-button>
        </div>
      </div>
    </div>

    <search-nav class="py-2" :useSearchButton="false">
      <search-year
        label="연도"
        v-model="nav.year"
        :options="{
          default: new Date().getFullYear(),
        }"
      />
      <search-select
        label="사업장명"
        v-model="nav.사업장명"
        :options="{
          default: '',
          items: nav.items.사업장명,
        }"
      />
      <search-select
        label="직영/수급업체"
        v-model="nav.구분"
        :options="{
          default: '',
          items: nav.items.구분,
        }"
      />
      <search-select
        label="팀명"
        v-model="nav.팀명"
        :options="{
          default: '',
          items: nav.items.팀명,
        }"
      />
      <search-select
        label="공장명"
        v-model="nav.공장명"
        :options="{
          default: '',
          items: nav.items.공장명,
        }"
      />
      <search-select
        label="상태"
        v-model="nav.상태"
        :options="{
          default: '',
          items: nav.items.상태,
        }"
      />
    </search-nav>

    <div class="flex items-center justify-end mb-4">
      <vs-button color="secondary" type="border" @click="onClickExcelDownload">Excel 다운로드</vs-button>
    </div>

    <div class="overflow-x-auto">
      <table class="w-full" style="min-width: 1598px">
        <colgroup>
          <col width="63px" />
          <col width="78px" />
          <col width="78px" />
          <col width="78px" />
          <col width="78px" />
          <col width="78px" />
          <col width="78px" />
          <col width="78px" />
          <col width="78px" />
          <col width="78px" />
          <col width="78px" />
          <col width="46px" />
          <col width="46px" />
          <col width="46px" />
          <col width="78px" />
          <col width="46px" />
          <col width="46px" />
          <col width="46px" />
          <col width="78px" />
          <col width="78px" />
          <col width="78px" />
          <col width="78px" />
        </colgroup>
        <thead>
          <tr>
            <th colspan="24" class="text-left">
              <span>발생빈도 5점 또는 단위위험(위험도) 10점 이상 : </span>
              <span>{{ filterList.length }}건</span>
            </th>
          </tr>
          <tr>
            <th rowspan="2" class="table-cell">No.</th>
            <th rowspan="2" class="table-cell">상태</th>
            <th rowspan="2" class="table-cell">사업장명</th>
            <th rowspan="2" class="table-cell">구분</th>
            <th rowspan="2" class="table-cell">팀명</th>
            <th rowspan="2" class="table-cell">공장명</th>
            <th rowspan="2" class="table-cell">대분류</th>
            <th rowspan="2" class="table-cell">중분류</th>
            <th rowspan="2" class="table-cell">소분류</th>
            <th rowspan="2" class="table-cell">위험요인</th>
            <th rowspan="2" class="table-cell">현재관리상태</th>
            <th colspan="3" class="table-cell">위험도(개선 전)</th>
            <th rowspan="2" class="table-cell">감소대책</th>
            <th colspan="3" class="table-cell">위험도(개선 후)</th>
            <th rowspan="2" class="table-cell">개선결과</th>
            <th rowspan="2" class="table-cell">담당</th>
            <th rowspan="2" class="table-cell">작성일자</th>
            <th rowspan="2" class="table-cell">비고</th>
          </tr>
          <tr>
            <th>빈도</th>
            <th>강도</th>
            <th>종합</th>
            <th>빈도</th>
            <th>강도</th>
            <th>종합</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="filterList.length === 0">
            <td colspan="24" class="empty-data">
              <div class="empty-data-content">
                <p class="empty-data-icon">📋</p>
                <p class="empty-data-text">조회된 데이터가 없습니다.</p>
                <p class="empty-data-hint">검색 조건을 변경해보세요.</p>
              </div>
            </td>
          </tr>
          <tr v-for="(row, rowIndex) in filterList" :key="`${row.raw.id}-${row.raw.post_id}-${rowIndex}`">
            <!-- No. -->
            <td>{{ filterList.length - rowIndex }}</td>
            <!-- 상태-->
            <td>
              <span v-if="row.상태 === '개선 전'" class="text-danger">개선 전</span>
              <span v-if="row.상태 === '결재중'" class="text-danger">결재중</span>
              <span v-if="row.상태 === '개선 완료'" class="text-success">개선 완료</span>
            </td>
            <!-- 사업장 -->
            <td>{{ row.사업장명 }}</td>
            <!-- 구분 -->
            <td>{{ row.구분 }}</td>
            <!-- 팀명 -->
            <td>{{ row.팀명 }}</td>
            <!-- 공장명 -->
            <td>{{ row.공장명 }}</td>
            <!-- 대분류 -->
            <td>{{ row.대분류 }}</td>
            <!-- 중분류 -->
            <td>{{ row.중분류 }}</td>
            <!-- 소분류 -->
            <td>{{ row.소분류 }}</td>
            <!-- 위험요인 -->
            <td>{{ row.위험요인 }}</td>
            <!-- 현재관리상태 -->
            <td>{{ row.현재관리상태 }}</td>
            <!-- 위험도(개선 전) 빈도 -->
            <td>{{ row.위험도_개선전_빈도 }}</td>
            <!-- 위험도(개선 전) 강도 -->
            <td>{{ row.위험도_개선전_강도 }}</td>
            <!-- 위험도(개선 전) 종합 -->
            <td>{{ row.위험도_개선전_종합 }}</td>
            <!-- 감소대책 -->
            <td>
              <p>{{ row.감소대책 }}</p>
              <vs-button class="px-2 py-1" color="#7E72F2" style="text-wrap: nowrap" @click="onClickPost(row, 'first')">원문보기</vs-button>
            </td>
            <!-- 위험도(개선 후) 빈도 -->
            <td>{{ row.위험도_개선후_빈도 }}</td>
            <!-- 위험도(개선 후) 강도 -->
            <td>{{ row.위험도_개선후_강도 }}</td>
            <!-- 위험도(개선 후) 종합 -->
            <td>{{ row.위험도_개선후_종합 }}</td>
            <!-- 개선결과 -->
            <td>
              <p>{{ row.개선결과 }}</p>
              <vs-button v-if="row.개선결과 !== '-'" class="px-2 py-1" color="#7E72F2" style="text-wrap: nowrap" @click="onClickPost(row, 'last')">원문보기</vs-button>
            </td>
            <!-- 담당 -->
            <td>{{ row.담당 }}</td>
            <!-- 작성일자 -->
            <td>{{ row.작성일자 }}</td>
            <!-- 비고 -->
            <td>
              <vs-button v-if="row.비고 === '개정'" class="px-2 py-1" style="text-wrap: nowrap" color="#FF9F43" @click="onClickResend(row)">개정</vs-button>
              <vs-button v-if="row.비고 === '작성 중'" class="px-2 py-1" style="text-wrap: nowrap" color="#28A745" @click="onClickPendingAppr(row)">작성 중</vs-button>
              <vs-button v-if="row.비고 === '결재 중'" class="px-2 py-1" style="text-wrap: nowrap" color="#1E90FF" @click="onClickPendingAppr(row)">결재 중</vs-button>
              <vs-button v-if="row.비고 === '종결'" class="px-2 py-1" style="text-wrap: nowrap" color="#3C3C3C">종결</vs-button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <popup-post-viewer v-model="postViewer" v-bind="propsPostViewer">
      <template #foot="{ close }">
        <div class="p-3 flex justify-end border-top">
          <div class="flex">
            <vs-button class="mr-2" color="#DCDFE2" style="color: #3c3c3c" @click="close">취소</vs-button>
            <vs-button color="#7E72F2" @click="onClickResend(propsPostViewer.row)">개정</vs-button>
          </div>
        </div>
      </template>
    </popup-post-viewer>
  </div>
</template>

<script>
import SearchNav from "@/components/nav/SearchNav.vue";
import SearchNavYearMonth from "@/components/nav/SearchNavYearMonth.vue";
import PopupPostViewer from "@/popup/PopupPostViewer.vue";
import SearchYear from "@/views/list/components/SearchYear.vue";
import SearchSelect from "@/views/list/components/SearchSelect.vue";
import XLSX from "xlsx-js-style";

export default {
  name: "Page",
  components: { SearchSelect, SearchYear, PopupPostViewer, SearchNavYearMonth, SearchNav },

  computed: {
    projectId() {
      return this.$route.params.proj_id;
    },
    boardId() {
      return this.$route.params.boardId;
    },
    filterList() {
      const list = this.list.filter((item) => {
        if (this.nav.사업장명 && this.nav.사업장명 !== item.사업장명) return false;
        if (this.nav.공장명 && this.nav.공장명 !== item.공장명) return false;
        if (this.nav.팀명 && this.nav.팀명 !== item.팀명) return false;
        if (this.nav.구분 && this.nav.구분 !== item.raw.board_id) return false;
        if (this.nav.상태 && this.nav.상태 !== item.상태) return false;
        return true;
      });
      return this.removeDuplicateExceptRaw(list);
    },
  },
  watch: {
    "nav.year": {
      handler() {
        this.loadList();
      },
      deep: true,
    },
    "nav.사업장명": {
      handler(newVal) {
        // 사업장명이 변경되면 관련된 공장명 필터링
        if (newVal) {
          const 관련공장 = [{ text: "전체", value: "" }];
          this.list.forEach((item) => {
            if (item.사업장명 === newVal && 관련공장.findIndex((option) => option.value === item.공장명) === -1 && item.공장명 !== "-") {
              관련공장.push({ text: item.공장명, value: item.공장명 });
            }
          });
          this.nav.items.공장명 = 관련공장;

          // 사업장명에 따른 팀명 필터링도 함께 업데이트
          const 관련팀 = [{ text: "전체", value: "" }];
          this.list.forEach((item) => {
            if (item.사업장명 === newVal && 관련팀.findIndex((option) => option.value === item.팀명) === -1 && item.팀명 !== "-") {
              관련팀.push({ text: item.팀명, value: item.팀명 });
            }
          });
          this.nav.items.팀명 = 관련팀;
        } else {
          // 사업장명이 선택되지 않았을 때 모든 공장명 표시
          const 모든공장 = [{ text: "전체", value: "" }];
          this.list.forEach((item) => {
            if (모든공장.findIndex((option) => option.value === item.공장명) === -1 && item.공장명 !== "-") {
              모든공장.push({ text: item.공장명, value: item.공장명 });
            }
          });
          this.nav.items.공장명 = 모든공장;

          // 모든 팀명 표시
          const 모든팀 = [{ text: "전체", value: "" }];
          this.list.forEach((item) => {
            if (모든팀.findIndex((option) => option.value === item.팀명) === -1 && item.팀명 !== "-") {
              모든팀.push({ text: item.팀명, value: item.팀명 });
            }
          });
          this.nav.items.팀명 = 모든팀;
        }
        // 공장명 초기화
        this.nav.공장명 = "";
        // 팀명도 초기화 필요
        this.nav.팀명 = "";
      },
    },
    "nav.팀명": {
      handler(newVal) {
        // 팀명이 변경되면 관련된 공장명 필터링
        if (newVal) {
          const 관련공장 = [{ text: "전체", value: "" }];
          this.list.forEach((item) => {
            if ((!this.nav.사업장명 || item.사업장명 === this.nav.사업장명) && item.팀명 === newVal && 관련공장.findIndex((option) => option.value === item.공장명) === -1 && item.공장명 !== "-") {
              관련공장.push({ text: item.공장명, value: item.공장명 });
            }
          });
          this.nav.items.공장명 = 관련공장;
        } else {
          // 팀명이 선택되지 않았을 때 사업장명에 관련된 공장명 표시
          const 관련공장 = [{ text: "전체", value: "" }];
          this.list.forEach((item) => {
            if ((!this.nav.사업장명 || item.사업장명 === this.nav.사업장명) && 관련공장.findIndex((option) => option.value === item.공장명) === -1 && item.공장명 !== "-") {
              관련공장.push({ text: item.공장명, value: item.공장명 });
            }
          });
          this.nav.items.공장명 = 관련공장;
        }
        // 공장명 초기화
        this.nav.공장명 = "";
      },
    },
  },
  data() {
    return {
      nav: {
        year: "",
        사업장명: "",
        공장명: "",
        팀명: "",
        구분: "",
        상태: "",

        items: {
          사업장명: [{ text: "전체", value: "" }],
          공장명: [{ text: "전체", value: "" }],
          팀명: [{ text: "전체", value: "" }],
          구분: [{ text: "전체", value: "" }],
          상태: [
            { text: "전체", value: "" },
            { text: "개선 전", value: "개선 전" },
            { text: "결재중", value: "결재중" },
            { text: "개선 완료", value: "개선 완료" },
          ],
        },
      },
      list: [],
      pendingApprList: [],
      postViewer: false,
      propsPostViewer: { title: "" },

      // 프로그레스 로딩 상태
      progressLoading: {
        show: false,
        title: "로딩 중...",
        message: "",
        progress: 0,
        retryInfo: "",
      },

      authorInfoPopup: {
        show: false,
        authorText: "",
        statusText: "",
        documentStatusId: null,
        boardId: null,
        postId: null,
      },
    };
  },

  methods: {
    async loadList() {
      const perPage = 100;

      this.showProgressLoading(true, "위험성평가 데이터 로딩", "데이터를 불러오는 중...");

      try {
        // 네트워크 오류 시 재시도 로직 포함
        const pendingApprResult = await this.retryApiCall(() => this._loadList(perPage, [1, 2, 3]), 3);
        this.pendingApprList = pendingApprResult;
        let pendingApprIds = [];
        if (0 < pendingApprResult.length) pendingApprIds = pendingApprResult.map((tmp) => tmp.post_parent_id).filter((id) => id !== null && id !== undefined && id > 0);
        const list = await this.retryApiCall(() => this._loadList(perPage, [4]), 3);

        this.updateProgress(70, "데이터 변환 중...");
        this.list = this.convertPostToList(list, pendingApprIds, this.pendingApprList);

        this.updateProgress(100, "완료");

        // 잠시 완료 상태 표시 후 로딩 종료
        setTimeout(() => {
          this.showProgressLoading(false);
        }, 500);
      } catch (error) {
        this.showProgressLoading(false);
        this.showErrorMessage("데이터를 불러오는 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
        console.error("loadList error:", error);
      }
    },

    convertPostToList(list, pendingApprIds, pendingApprList = []) {
      const isWarning = function (item) {
        let isWarning = false;
        const 발생빈도 = Number(item.발생빈도 || 0);
        const 위험강도 = Number(item.위험강도 || 0);
        const 위험도 = 발생빈도 * 위험강도;
        if (발생빈도 >= 5 || 위험도 >= 10) isWarning = true;
        return isWarning;
      };
      const isAfterWarning = function (item) {
        let isWarning = false;
        const 발생빈도 = Number(item.개선후_발생빈도 || 0);
        const 위험강도 = Number(item.개선후_위험강도 || 0);
        const 위험도 = 발생빈도 * 위험강도;
        if (발생빈도 >= 5 || 위험도 >= 10 || 위험도 === 0) isWarning = true;
        return isWarning;
      };

      // 기본 필터 옵션 초기화
      const 사업장명 = [{ text: "전체", value: "" }];
      const 공장명 = [{ text: "전체", value: "" }];
      const 팀명 = [{ text: "전체", value: "" }];
      const 구분 = [{ text: "전체", value: "" }];
      const result = [];
      if (list.length > 0) {
        for (const item of list) {
          const body = item.postContents.body;
          const boardId = item.board_id;

          if (body.위험성평가표.data && body.위험성평가표.data.length > 0) {
            for (const data of body.위험성평가표.data) {
              const 개선전중요위험 = isWarning(data);
              const 개선후중요위험 = isAfterWarning(data);

              if (개선전중요위험) {
                const 현재사업장 = body.stdInfo.field_name || "-";
                const 현재공장 = body.위험성평가정보.공장 || "-";
                const 현재팀 = body.위험성평가정보.팀 || "-";

                // 사업장명 추가
                if (사업장명.findIndex((item) => item.value === 현재사업장) === -1 && 현재사업장 !== "-") {
                  사업장명.push({ text: 현재사업장, value: 현재사업장 });
                }

                // 공장명 추가
                if (공장명.findIndex((item) => item.value === 현재공장) === -1 && 현재공장 !== "-") {
                  공장명.push({ text: 현재공장, value: 현재공장 });
                }

                // 팀명 추가
                if (팀명.findIndex((item) => item.value === 현재팀) === -1 && 현재팀 !== "-") {
                  팀명.push({ text: 현재팀, value: 현재팀 });
                }

                // 구분 추가
                if (구분.findIndex((item) => item.value === boardId) === -1) {
                  구분.push({ text: boardId === 813 ? "직영" : "수급업체", value: boardId });
                }

                const docId = Number(item.post_id);
                const isInPending = pendingApprIds.includes(docId);
                // 자식 문서 중 결재 중(status 2,3)인 항목이 하나라도 있으면 "결재 중", 임시저장(status 1)만 있으면 "작성 중"
                const pendingChildren = isInPending ? pendingApprList.filter((p) => Number(p.post_parent_id) === docId) : [];
                const hasApprovalInProgress = pendingChildren.some((p) => p.document_status_id === 2 || p.document_status_id === 3);
                const hasDraft = pendingChildren.some((p) => p.document_status_id === 1);
                const 상태 = 개선후중요위험 ? (isInPending ? "결재중" : "개선 전") : "개선 완료";
                const 비고 = 개선후중요위험 ? (isInPending ? (hasApprovalInProgress ? "결재 중" : (hasDraft ? "작성 중" : "결재 중")) : "개정") : "종결";

                result.push({
                  상태,
                  사업장명: 현재사업장,
                  공장명: 현재공장,
                  구분: boardId === 813 ? "직영" : "수급업체",
                  팀명: 현재팀,
                  대분류: body.공정분류.대분류 || "-",
                  중분류: body.공정분류.중분류 || "-",
                  소분류: body.공정분류.소분류 || "-",
                  위험구분: data.분류 || data.작업단계 || "-",
                  원인: data.원인 || "-",
                  위험요인: data.위험요인 || "-",
                  현재관리상태: data.현재조치관리상태 || "-",
                  위험도_개선전_빈도: data.발생빈도,
                  위험도_개선전_강도: data.위험강도,
                  위험도_개선전_종합: Number(`${data.발생빈도}`) * Number(`${data.위험강도}`),
                  감소대책: data.감소대책 || "-",
                  위험도_개선후_빈도: data.개선후_발생빈도 || "-",
                  위험도_개선후_강도: data.개선후_위험강도 || "-",
                  위험도_개선후_종합: isNaN(Number(`${data.개선후_발생빈도}`) * Number(`${data.개선후_위험강도}`)) ? "-" : Number(`${data.개선후_발생빈도}`) * Number(`${data.개선후_위험강도}`),
                  개선결과: data.개선결과 || "-",
                  담당: data.담당자 || "-",
                  작성일자: new Date(item.postContents.created_at).format("yyyy-MM-dd") || "-",
                  비고,
                  raw: item,
                });
              }
            }
          }
        }
      }

      // 전체 목록 반환
      this.nav.items.사업장명 = 사업장명;
      this.nav.items.공장명 = 공장명;
      this.nav.items.팀명 = 팀명;
      this.nav.items.구분 = 구분;

      return result;
    },

    async _loadList(perPage, statusIds) {
      const projectId = this.projectId;
      const page = 1;
      const boardId = 813;

      // 결재 대기(status 1,2,3) 조회 시에는 연도 조건 제외:
      // 자식 문서(개정본)는 원본의 평가일자 연도와 다른 연도에 생성될 수 있음
      const conditions = [];
      if (statusIds.includes(4)) {
        conditions.push({ key: "created_at", operator: ">=", value: `${this.nav.year}-01-01` });
        conditions.push({ key: "created_at", operator: "<", value: `${Number(this.nav.year) + 1}-01-01` });
      }
      conditions.push({ key: "body.위험성평가표.중요위험", operator: "=", value: "Y" });
      conditions.push({
        boolean: "AND",
        conditions: [
          { key: "body.위험성평가표.data[%].위험도", operator: ">=", value: 10 },
          { key: "body.위험성평가표.data[%].발생빈도", operator: ">=", value: 5, boolean: "OR" },
        ],
      });

      const metaKeys = ["created_at"];
      if (statusIds.includes(4)) {
        metaKeys.push("body.stdInfo.field_name");
        metaKeys.push("body.위험성평가정보.%");
        metaKeys.push("body.공정분류.%");
        metaKeys.push("body.위험성평가표.중요위험");
        metaKeys.push("body.위험성평가표.data[%].분류");
        metaKeys.push("body.위험성평가표.data[%].작업단계");
        metaKeys.push("body.위험성평가표.data[%].원인");
        metaKeys.push("body.위험성평가표.data[%].위험요인");
        metaKeys.push("body.위험성평가표.data[%].현재조치관리상태");
        metaKeys.push("body.위험성평가표.data[%].발생빈도");
        metaKeys.push("body.위험성평가표.data[%].위험강도");
        metaKeys.push("body.위험성평가표.data[%].감소대책");
        metaKeys.push("body.위험성평가표.data[%].개선후_발생빈도");
        metaKeys.push("body.위험성평가표.data[%].개선후_위험강도");
        metaKeys.push("body.위험성평가표.data[%].개선결과");
        metaKeys.push("body.위험성평가표.data[%].담당자");
      } else {
        conditions.push({ key: "post_parent_id", operator: ">", value: "0" });
        metaKeys.push("author.id");
        metaKeys.push("author.name");
        metaKeys.push("author.section");
        metaKeys.push("author.department");
        metaKeys.push("author.rank");
        metaKeys.push("author.position");
        metaKeys.push("author.company_id");
        metaKeys.push("author.company.com_name");
      }

      const options = {
        projectId,
        boardSlug: `board-${boardId}`,
        isLeaf: true, // 가장 마지막 자식 문서들만 조회
        page,
        perPage,
        withMeta: true,
        withoutRelation: true,
        onlyMeta: true,
        metaKeys,
        statusIds, // 4:승인
        conditions,
        withSlugs: ["board-814"],
      };
      if (statusIds.includes(4)) {
        options.isLeaf = true;
      }
      return await this._loadDefragmentation(options);
    },

    // options.page 에 설정된 페이지부터 마지막 페이지까지 데이터를 불러옴
    async _loadDefragmentation(options) {
      const response = await this.$store.dispatch("approval/LOAD_POST_ALL", options);
      const list = [];
      list.push(...response.data);

      // 페이지별 진행률 계산 및 표시
      const currentPage = response.current_page;
      const lastPage = response.last_page;
      const progress = Math.round((currentPage / lastPage) * 60); // 전체 60%까지가 데이터 로딩
      this.updateProgress(progress, `데이터 조회 중... (${currentPage}/${lastPage})`);

      if (response.current_page !== response.last_page) {
        options.page = response.current_page + 1;
        list.push(...(await this._loadDefragmentation(options)));
      }
      return list;
    },

    // 재시도 로직이 포함된 API 호출
    async retryApiCall(apiCall, maxRetries = 3) {
      let lastError;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const result = await apiCall();
          return result;
        } catch (error) {
          lastError = error;
          console.warn(`API 호출 실패 (${attempt}/${maxRetries}):`, error);

          if (attempt < maxRetries) {
            // 재시도 전 잠시 대기 (exponential backoff)
            const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
            this.updateProgress(this.progressLoading.progress, `네트워크 오류 발생. ${delay / 1000}초 후 재시도...`, `재시도 ${attempt}/${maxRetries}`);
            await new Promise((resolve) => setTimeout(resolve, delay));
          }
        }
      }

      throw lastError;
    },

    // 프로그레스 로딩 표시
    showProgressLoading(show, title = "로딩 중...", message = "") {
      this.progressLoading.show = show;
      this.progressLoading.title = title;
      this.progressLoading.message = message;
      this.progressLoading.progress = 0;
      this.progressLoading.retryInfo = "";
    },

    // 프로그레스 업데이트
    updateProgress(progress, message = "", retryInfo = "") {
      this.progressLoading.progress = Math.min(progress, 100);
      this.progressLoading.message = message;
      this.progressLoading.retryInfo = retryInfo;
    },

    // 에러 메시지 표시
    showErrorMessage(message) {
      const customAlert = new CustomEvent("customAlert", {
        detail: {
          title: "오류",
          message,
          useConfirm: false,
          confirmBtn: "확인",
        },
      });
      window.dispatchEvent(customAlert);
    },

    loading(open) {
      const loadingDialog = new CustomEvent("loadingDialog", {
        detail: {
          open,
        },
      });
      window.dispatchEvent(loadingDialog);
    },

    // 커스텀 확인 다이얼로그
    customConfirm(msg, title, onConfirm) {
      const customAlert = new CustomEvent("customAlert", {
        detail: {
          title: title || "확인",
          message: msg,
          useConfirm: true,
          confirmBtn: "개정",
          cancelBtn: "취소",
          confirmFunction: onConfirm,
        },
      });
      window.dispatchEvent(customAlert);
    },

    onClickOpenDocument() {
      const { boardId, postId } = this.authorInfoPopup;
      const projectId = this.projectId;
      const url = `${window.location.origin}/project/${projectId}/tab/${boardId}/write/${postId}`;
      window.open(url, "_blank");
    },

    onClickPendingAppr(row) {
      const parentId = row.raw.post_id;
      const pendingItem = this.pendingApprList.find((item) => item.post_parent_id === parentId);
      if (!pendingItem) return;
      const author = pendingItem.postContents ? pendingItem.postContents.author : null;
      const statusMap = { 1: "임시저장", 2: "등록됨", 3: "결재 중" };
      if (author) {
        const section = author.section || "";
        const department = author.department || "";
        const locationParts = section === department ? [section] : [section, department];
        const parts = [...locationParts.filter(Boolean), author.name || "", author.rank || ""].filter(Boolean);
        this.authorInfoPopup.authorText = parts.join(" ");
      } else {
        this.authorInfoPopup.authorText = "";
      }
      this.authorInfoPopup.statusText = statusMap[pendingItem.document_status_id] || "알 수 없음";
      this.authorInfoPopup.documentStatusId = pendingItem.document_status_id;
      this.authorInfoPopup.boardId = pendingItem.board_id;
      this.authorInfoPopup.postId = pendingItem.post_id;
      this.authorInfoPopup.show = true;
    },

    async onClickResend(row) {
      const projectId = row.raw.project_id;
      const boardId = row.raw.board_id;
      const targetPostId = row.raw.post_id;

      // targetPostId 문서 상세 내용을 API에서 불러온다
      const targetPost = await this.$store.dispatch("board/LOAD_POST", {
        boardId,
        postId: targetPostId,
      });

      const parentId = targetPost.id;
      const content = JSON.parse(targetPost.post_content);

      // 임시저장 문서가 없을 경우 개정 후 id 가져옴
      this.customConfirm("정말 개정하시겠습니까?", "알림", async () => {
        // loadList()에서 이미 조회한 [1,2,3] 상태 데이터를 재사용
        let list = [...this.pendingApprList];
        // 임시저장, 등록됨, 진행중 인 문서 가져옴
        list = list.filter((item) => {
          if (item.post_parent_id === parentId) {
            return item.document_status_id === 1 || item.document_status_id === 2 || item.document_status_id === 3;
          }
          return false;
        });
        let postId = 0;
        if (list.length > 0) {
          const firstPost = await this.$store.dispatch("board/LOAD_POST", {
            boardId: list[0].board_id,
            postId: list[0].post_id,
          });
          const author = firstPost.author; // 실제 작성자 정보
          const body = JSON.parse(firstPost.post_content).body; // 내용상 작성자 정보 (개정시 작성자 정보도 복사하여 오기 때문에 실제 작성자와 다른 문제가 있다)
          if (author.name !== this.$store.state.auth.userInfo.name) {
            alert(`현재 진행중인 개정은 작성자만 가능합니다.\n\n<<현재 문서의 작성자 정보>>\n소속명: ${author.section}\n작성자: ${author.name}`);
            return;
          }
          postId = list[0].post_id;
        }
        if (!postId) {
          await this.tempResend(projectId, boardId, parentId, content);
          return;
        }
        // 임시저장 문서로 이동
        this.$router.push(`/project/${projectId}/tab/${boardId}/write/${postId}?_t_back=816/page`);
      });
    },

    // 현재 단위위험이 일반에 해당하는 행은, 개선 후 값을 현재 값으로 미리 채워둔다.
    // (개정 화면에서 해당 행은 readonly 라 사용자가 직접 입력할 수 없다)
    seedImprovementValues(rows) {
      if (!Array.isArray(rows)) return;
      for (const row of rows) {
        if (row.위험도 < 10 && row.발생빈도 < 5) {
          row["개선후_발생빈도"] = row["발생빈도"];
          row["개선후_위험강도"] = row["위험강도"];
          row["개선후_단위위험"] = row["단위위험"];
        }
      }
    },

    // 위험성평가표.data 는 DKRAS.data + JSA.data 로부터 파생되는 병합 배열이라
    // 작성 화면 진입 시 _rebuildMergedData() 로 다시 만들어진다.
    // 따라서 실데이터인 DKRAS.data / JSA.data 를 고쳐야 한다.
    // (DKRAS/JSA 구조가 없는 구 포맷 문서는 data 가 유일한 실데이터이므로 그대로 사용)
    seedImprovement(평가표) {
      if (!평가표) return;
      if (평가표.DKRAS || 평가표.JSA) {
        this.seedImprovementValues(평가표.DKRAS && 평가표.DKRAS.data);
        this.seedImprovementValues(평가표.JSA && 평가표.JSA.data);
      } else {
        this.seedImprovementValues(평가표.data);
      }
    },

    // 임시저장하지 않고 write 상태인 개정
    async tempResend(projectId, boardId, parentId, form) {
      const body = form.body;

      form.body.위험성평가정보.평가일자 = new Date().format("yyyy-MM-dd");
      const userInfo = this.$store.state.auth.userInfo;
      form.body.stdInfo.section = userInfo.section; //소속1
      form.body.stdInfo.department = userInfo.department; //소속2
      form.body.stdInfo.rank = userInfo.rank; // 직책
      form.body.stdInfo.writer_name = userInfo.name; // 작성자
      form.body.stdInfo.date = new Date().format("yyyy-MM-dd"); // 작성일

      this.seedImprovement(body.위험성평가표);

      localStorage.setItem(`tempResend_${boardId}_${parentId}`, JSON.stringify(form));

      const query = {
        ...this.$route.query,
        _p_pid: parentId,
        _t_back: "816/page",
      };

      //_cp_pid: postId,
      await this.$router.push({ name: "write|tab", params: { pageType: "write", proj_id: projectId, boardId }, query });
    },

    async onClickPost(row, firstLast) {
      const raw = row.raw;
      const boardId = raw.board_id;
      this.propsPostViewer.boardId = boardId;
      this.propsPostViewer.body = {};
      this.propsPostViewer.row = row;
      // "개선결과" 원문보기(last)일 때만 개선후 위험도 UI를 강제로 노출 (813/814 Write.vue 전용 옵션)
      this.propsPostViewer.options = { forceShowImprovement: firstLast === "last" };

      if (boardId === 813) this.propsPostViewer.title = "위험성평가(직영)";
      if (boardId === 814) this.propsPostViewer.title = "위험성평가(수급업체)";

      let targetPostId = raw.post_id;
      this.propsPostViewer.parentId = undefined;

      if (firstLast === "first" && raw.post_parent_id) {
        targetPostId = raw.post_parent_id;
      } /* 현재 문서가 최초 문서인 경우 */ else if (firstLast === "last") {
        this.propsPostViewer.parentId = raw.post_parent_id;
      }

      // targetPostId 문서 상세 내용을 API에서 불러온다
      const targetPost = await this.$store.dispatch("board/LOAD_POST", {
        boardId,
        postId: targetPostId,
      });

      this.propsPostViewer.body = JSON.parse(targetPost.post_content).body;

      this.postViewer = true;
    },

    onClickExcelDownload() {
      // 새로운 워크북 생성
      const wb = XLSX.utils.book_new();

      // 헤더 데이터 준비
      const headers = [
        ["위험성평가표 위험도 목록"],
        [
          "No.",
          "상태",
          "사업장명",
          "구분",
          "팀명",
          "공장명",
          "대분류",
          "중분류",
          "소분류",
          "위험요인",
          "현재관리상태",
          "위험도(개선 전) 빈도",
          "위험도(개선 전) 강도",
          "위험도(개선 전) 종합",
          "감소대책",
          "위험도(개선 후) 빈도",
          "위험도(개선 후) 강도",
          "위험도(개선 후) 종합",
          "개선결과",
          "담당",
          "작성일자",
        ],
      ];

      // 데이터 준비 - 셀 형식으로 직접 스타일 지정
      const data = this.filterList.map((row, index) => {
        const rowData = [];

        // No.
        rowData.push({
          v: index + 1,
          t: "n",
          s: {
            alignment: { horizontal: "center", vertical: "center" },
            border: { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } },
          },
        });

        // 상태
        const statusStyle = {
          alignment: { horizontal: "center", vertical: "center" },
          border: { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } },
          font: { bold: true },
        };

        if (row.상태 === "개선 전") {
          statusStyle.font.color = { rgb: "FF0000" }; // 빨간색
        } else if (row.상태 === "개선 완료") {
          statusStyle.font.color = { rgb: "008000" }; // 녹색
        }

        rowData.push({ v: row.상태, t: "s", s: statusStyle });

        // 나머지 열들에 대한 기본 스타일
        const defaultStyle = {
          alignment: { horizontal: "center", vertical: "center" },
          border: { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } },
        };

        // 사업장명 ~ 작성일자
        [
          row.사업장명,
          row.공장명,
          row.구분,
          row.팀명,
          row.대분류,
          row.중분류,
          row.소분류,
          row.위험구분,
          row.원인,
          row.위험요인,
          row.현재관리상태,
          row.위험도_개선전_빈도,
          row.위험도_개선전_강도,
          row.위험도_개선전_종합,
          row.감소대책,
          row.위험도_개선후_빈도,
          row.위험도_개선후_강도,
          row.위험도_개선후_종합,
          row.개선결과,
          row.담당,
          row.작성일자,
        ].forEach((value) => {
          rowData.push({ v: value, t: "s", s: defaultStyle });
        });

        return rowData;
      });

      // 헤더 행에 스타일 적용
      const titleRow = [
        {
          v: "위험성평가표 위험도 목록",
          t: "s",
          s: {
            font: { bold: true, sz: 14 },
            alignment: { horizontal: "center", vertical: "center" },
          },
        },
      ];

      // 23개의 빈 셀을 추가하여 병합에 사용
      for (let i = 0; i < 22; i++) {
        titleRow.push({ v: "", t: "s" });
      }

      // 헤더 행 스타일 적용
      const headerRow = [];
      headers[1].forEach((header) => {
        headerRow.push({
          v: header,
          t: "s",
          s: {
            font: { bold: true },
            alignment: { horizontal: "center", vertical: "center" },
            fill: { fgColor: { rgb: "FFFF00" }, type: "pattern", patternType: "solid" }, // 노란색 배경
            border: { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } },
          },
        });
      });

      // 스타일이 적용된 데이터로 워크시트 생성
      const ws = XLSX.utils.aoa_to_sheet([titleRow, headerRow]);

      // 데이터 행 추가
      data.forEach((row, idx) => {
        XLSX.utils.sheet_add_aoa(ws, [row.map((cell) => cell.v)], { origin: -1 });

        // 각 셀에 스타일 적용
        const rowIndex = idx + 2; // 헤더 두 줄 다음부터 시작
        row.forEach((cell, colIndex) => {
          const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
          ws[cellRef].s = cell.s;
        });
      });

      // 제목 병합
      ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 22 } }];

      // 열 너비 설정
      ws["!cols"] = Array(23).fill({ width: 15 });

      // 워크북에 워크시트 추가
      XLSX.utils.book_append_sheet(wb, ws, "위험성평가표");

      // 파일 이름 생성
      const fileName = `위험성평가표_위험도_목록_${this.nav.year || new Date().getFullYear()}.xlsx`;

      // 엑셀 파일 다운로드
      XLSX.writeFile(wb, fileName);
    },

    // 리스트 중복 제거
    removeDuplicateExceptRaw(arr) {
      const seen = new Set();

      return arr.filter((item) => {
        // raw 제외한 객체 생성
        const { raw, ...rest } = item;

        // key 정렬해서 문자열로 변환 (순서 차이 방지)
        const key = JSON.stringify(
          Object.keys(rest)
            .sort()
            .reduce((acc, k) => {
              acc[k] = rest[k];
              return acc;
            }, {})
        );

        if (seen.has(key)) {
          return false; // 중복
        }

        seen.add(key);
        return true;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
table {
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    border: 1px solid #cdcdcd;
    padding: 0.5rem;
    text-align: center;
  }
  th {
    background-color: #f9f9f9;
    padding: 1rem 0.75rem;
    font-size: 1rem;
    font-weight: bold;
    color: #3c3c3c;
  }
  td {
    background-color: #fff;
  }
}

.text-danger,
.text-success {
  font-size: 1rem;
  font-weight: bold;
}

/* 빈 데이터 스타일 */
.empty-data {
  padding: 3rem 1rem !important;
  background-color: #f9fafb !important;
}

.empty-data-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.empty-data-icon {
  font-size: 3rem;
  margin: 0;
  opacity: 0.5;
}

.empty-data-text {
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
  margin: 0;
}

.empty-data-hint {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

/* 프로그레스 로딩 스타일 */
.progress-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.progress-loading-dialog {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 400px;
  width: 90%;
  text-align: center;
}

.progress-loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #7e72f2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
}

.loading-message {
  margin: 0;
  color: #666;
  font-size: 0.95rem;
}

.progress-bar-container {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.progress-bar {
  flex: 1;
  height: 8px;
  background-color: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #7e72f2, #a855f7);
  border-radius: 4px;
  transition: width 0.3s ease;
  animation: progress-shimmer 1.5s infinite;
}

@keyframes progress-shimmer {
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
}

.progress-text {
  font-weight: 600;
  color: #7e72f2;
  min-width: 40px;
  font-size: 0.9rem;
}

.retry-info {
  color: #f59e0b;
  font-size: 0.85rem;
  margin-top: 0.5rem;
}

.retry-info small {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background: #fef3c7;
  border-radius: 4px;
  border: 1px solid #f59e0b;
}

.author-info-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.author-info-dialog {
  background: #fff;
  border-radius: 8px;
  padding: 1.5rem 2rem;
  min-width: 320px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
}

.author-info-row {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 0.6rem;
  font-size: 0.95rem;
  color: #333;
}

.author-info-label {
  font-weight: 600;
  min-width: 50px;
  color: #555;
}

.author-info-label::after {
  content: " :";
}

.author-info-value {
  flex: 1;
}

.author-info-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 1.2rem;
}
</style>
